import React from 'react';

import { formatDate } from '@betterment/core';
import { useMutation, useQuery } from '@betterment/js-runtime';
import { Box, Button, Flex, Tag, Text } from '@betterment/style-closet-react';
import { isAfter } from 'date-fns';

import Layout, { StyledCheckbox } from './Layout';
import LinkSent from './LinkSent';

import { CoPilotSectionProvider, useCoPilotSectionState } from '../../contexts/CoPilotSectionContext';
import { PendingInvite } from '../../services/api/types/co_pilot/pending_invites';
import { ActionRequestParams, CoPilotResponseMeta } from '../../utils/co_pilot';

const formattedInviteDate = (linkExpiresAt: string) => {
  const formattedDate = formatDate(linkExpiresAt, 'longOrdinalSlashed');
  if (isAfter(new Date(), new Date(linkExpiresAt))) {
    return (
      <>
        {formattedDate} <Text color="bmtGrey50">(Expired)</Text>
      </>
    );
  }
  return formattedDate;
};

const PendingInviteRow: React.FC<React.PropsWithChildren<{ pendingInvite: PendingInvite }>> = ({
  pendingInvite: {
    id,
    clientName,
    email,
    accountType,
    linkSentAt,
    linkExpiresAt,
    queuedAt,
    primaryAdvisorName,
    isSecondaryHousehold,
  },
}) => {
  const { isItemSelected, handleSelectedItemChange } = useCoPilotSectionState<PendingInvite>();

  return (
    <tr>
      <td>
        <Flex alignItems="center">
          <StyledCheckbox
            name={id}
            id={id}
            role="checkbox"
            type="checkbox"
            className="checkboxInputBase"
            checked={isItemSelected(id)}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => handleSelectedItemChange(target.id)}
          />
          <Text>{clientName}</Text>
        </Flex>
        <Box pl={9}>
          <Text color="bmtGrey50">{email}</Text>
        </Box>
        {isSecondaryHousehold && (
          <Tag size="large" ml={9} rounded={true}>
            <Text>Primary Advisor: {primaryAdvisorName}</Text>
          </Tag>
        )}
      </td>
      <td>{accountType}</td>
      <LinkSent linkSentAt={linkSentAt} queuedAt={queuedAt} />
      <td>{formattedInviteDate(linkExpiresAt)}</td>
    </tr>
  );
};

const PendingInviteTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { itemList, status, handleSelectAllChange, lastPage, loadMore } = useCoPilotSectionState<PendingInvite>();
  return (
    <table border={0}>
      <tbody>
        <tr>
          <Text as="th" fontWeight="bold" pb={6}>
            <Flex alignItems="center">
              <StyledCheckbox
                name="selectAll"
                id="selectAll"
                role="checkbox"
                type="checkbox"
                className="checkboxInputBase"
                checked={status === 'allSelected'}
                onChange={() => handleSelectAllChange()}
              />
              Client
            </Flex>
          </Text>
          <Text as="th" fontWeight="bold" pb={6}>
            Account
          </Text>
          <Text as="th" fontWeight="bold" pb={6}>
            Link sent
          </Text>
          <Text as="th" fontWeight="bold" pb={6}>
            Link expiration
          </Text>
        </tr>
        {itemList?.map(pendingInvite => (
          <PendingInviteRow key={pendingInvite.id} pendingInvite={pendingInvite} />
        ))}
        {status !== 'empty' && status !== 'fetchError' && !lastPage && (
          <tr>
            <td>
              <Box pb={9}>
                <Button variant="tertiary" onClick={() => loadMore()}>
                  Load more
                </Button>
              </Box>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const PendingInvites: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { refetch } = useQuery<PendingInvite[], CoPilotResponseMeta>(`/api/v1/co_pilot/pending_invites`, {
    skip: true,
  });
  const [postSendReminder] = useMutation<{}, ActionRequestParams>(`/api/v1/co_pilot/pending_invites/reminders`);
  const [postDismissal] = useMutation<{}, ActionRequestParams>('/api/v1/co_pilot/pending_invites/dismissals');

  return (
    <CoPilotSectionProvider
      fetch={refetch}
      initialOrder={[{ col: 'client_name', dir: 'desc' }]}
      postSendReminder={postSendReminder}
      postHandleDismissal={postDismissal}
    >
      <Layout
        activeTab="Pending invites"
        header="These clients have outstanding platform invites."
        subHeader="Platform invite links expire in 14 days from the date sent. You can send a reminder or deactivate any that are no longer relevant. Sending a reminder renews expired links."
      >
        <PendingInviteTable />
      </Layout>
    </CoPilotSectionProvider>
  );
};

export default PendingInvites;
